import { NormalizeOAS, OASRequestParams, OASResponse } from 'fets';
import type assetLibraryOpenApi from '../asset-library-open-api';
import { AssetLibraryFetsApiClient } from '../asset-library-fets-client';
import { assertNever } from '../utils';
import { Guid } from '../../api/public/utils/Guid';
import type { AssetDetail } from '../asset-library-types';

const PATH = '/api/v2/asset/company/{companyId}/asset/{assetId}/retry-processing';

type Params = {
  companyId: Guid;
  assetId: Guid;
};

export type RetryAssetProcessingResult =
  | { status: 'retried'; asset: AssetDetail }
  | { status: 'notFound' }
  | { status: 'notRetryable' };

export type ApiFunction = (params: Params, signal?: AbortSignal) => Promise<RetryAssetProcessingResult>;

export const createRetryAssetProcessing = (apiClient: AssetLibraryFetsApiClient): ApiFunction => {
  const retryAssetProcessing = async (params: Params, signal?: AbortSignal): Promise<RetryAssetProcessingResult> => {
    const response = await apiClient[PATH].post({
      ...convertToRequest(params),
      ...(signal && { signal }),
    });

    return convertResponse(response);
  };

  return retryAssetProcessing;
};

type Request = OASRequestParams<NormalizeOAS<typeof assetLibraryOpenApi>, typeof PATH, 'post'>;
type Response = OASResponse<NormalizeOAS<typeof assetLibraryOpenApi>, typeof PATH, 'post'>;

const convertToRequest = (params: Params): Request => ({
  params: { companyId: params.companyId, assetId: params.assetId },
});

const convertResponse = async (response: Response): Promise<RetryAssetProcessingResult> => {
  const responseStatus = response.status;
  if (responseStatus === 404) {
    return { status: 'notFound' };
  }

  if (responseStatus === 409) {
    return { status: 'notRetryable' };
  }

  if (responseStatus !== 200) {
    return assertNever(responseStatus);
  }

  const data = await response.json();
  const asset: AssetDetail = {
    id: data.id,
    name: data.name,
    size: data.size,
    uploadedAt: Number(data.uploadedAt),
    state: data.state,
    mediaUrl: data.mediaUrl,
    downloadUrl: data.downloadUrl,
    urlsExpireAt: Number(data.urlsExpireAt),
    thumbnailUrl: data.thumbnailUrl ?? null,
    dimensions: data.dimensions ?? null,
    attribution: data.attribution ?? null,
  };
  return { status: 'retried', asset };
};
